import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';

import CART_ITEM from '../../constants/cartItems';
import { CURRENCY } from '../../../../constants/currency';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import { getPhotoPricingPrintProductCartItemPrice } from '../../redux/helpers';
import CartItemPhotoPreview from './CartItemPhotoPreview';
import CheckoutItemQuantityInput from './CheckoutItemQuantityInput';
import TrashIcon from '../../../../components/icons/TrashIcon';
import PrinterIcon from '../../../../components/icons/PrinterIcon';
import CheckoutCartItemPriceDisplay from './CheckoutCartItemPriceDisplay';
import { PrintProductConfiguratorModal } from '../../../prints/components/PrintProductConfigurator';
import PrintProductSizeRoesConfiguratorModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/PrintProductsGroup/components/PrintProductSizeRoesConfigurator';
import Button from '../../../../components/global/Button';
import CartItemVendor from './CartItemVendor';
import { getAsset } from '../../../../util/assets';
import IdentityAvatar from '../../../../containers/global/identity/IdentityAvatarContainer';
import { getPhotoPricingPrintBackgrounds } from '../../../../util/photo-pricings';
import useIdentityJerseyColors from '../../../../apps/pro/views/HierarchyItem/Matches/hooks/useIdentityJerseyColors';

const propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([CART_ITEM.INDIVIDUAL_PRINT]).isRequired,
    printItem: PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
      price: PropTypes.shape({
        value: PropTypes.number.isRequired,
        currency: PropTypes.oneOf(Object.values(CURRENCY)),
      }).isRequired,
    }).isRequired,
    photo: PropTypes.shape({
      id: PropTypes.string.isRequired,
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string.isRequired,
          width: PropTypes.number.isRequired,
          height: PropTypes.number.isRequired,
          size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)).isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
    quantity: PropTypes.number.isRequired,
    watermark: PropTypes.bool.isRequired,
  }).isRequired,
  pricing: PropTypes.shape({
    price: PropTypes.object,
    discount_price: PropTypes.object,
  }),
  loading: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const PhotoPricingPrintProductCartItem = ({
  item,
  pricing,
  loading,
  onRemove,
  onUpdate,
  watermarkSettings,
}) => {
  const [configuringPrintProduct, setConfiguringPrintProduct] = useState(false);
  const {
    photoPricingPrintProduct,
    quantity,
    accountCard,
    photo,
    identity,
    albumMembershipGroup,
    roesConfiguration,
    isAllPhotos = false,
    photoPricing,
    prePaid: isPrepaid,
  } = item;

  const { jerseyColors } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId:
      albumMembershipGroup?.album_folder?.id ??
      albumMembershipGroup?.album?.root_album_folder?.id,
  });

  const price = getPhotoPricingPrintProductCartItemPrice(item);

  const { name: productName } = photoPricingPrintProduct ?? {};

  const printBackgrounds = photo?.foreground?.original_url
    ? getPhotoPricingPrintBackgrounds(
        photoPricing,
        albumMembershipGroup,
        jerseyColors,
      )
    : [];
  return (
    <Fragment>
      <div className="checkout-cart-item checkout-individual-print-cart-item">
        <div className="checkout-cart-item-content">
          <div className="checkout-cart-item-figure">
            {isPrepaid ? (
              <Fragment>
                <div className="purchasing-option-item-thumb">
                  <img
                    src={
                      photoPricingPrintProduct?.print_product
                        ?.preview_asset_image?.url ??
                      getAsset('/icon-custom-print.svg')
                    }
                    width="62px"
                    alt=""
                  />
                </div>
                <IdentityAvatar identity={identity} />
              </Fragment>
            ) : (
              <CartItemPhotoPreview
                photo={item.photo}
                watermarkSettings={watermarkSettings}
              >
                <span className="checkout-cart-item-photo-icon">
                  <PrinterIcon color="#0e84fe" size={18} />
                </span>
              </CartItemPhotoPreview>
            )}
          </div>

          <div className="checkout-cart-item-info">
            <span className="checkout-cart-item-title">
              {(productName ?? '').replace(
                ' (configured as rendered sheets)',
                '',
              )}
            </span>
            <CheckoutCartItemPriceDisplay
              clientPrice={price}
              pricing={pricing}
              loading={loading}
            />
            <CartItemVendor cartItem={item} />
            <div className="checkout-cart-item-info-actions mt-2">
              <CheckoutItemQuantityInput
                onChange={value =>
                  onUpdate(item.id, {
                    quantity: value,
                  })
                }
                value={quantity}
              />

              <button
                className="checkout-cart-item-btn-remove"
                onClick={() => onRemove(item.id)}
              >
                <TrashIcon color="#fe5851" size={18} />
              </button>
            </div>
          </div>
        </div>
        {!isPrepaid && (
          <Button
            btnSize="xs"
            onClick={() => setConfiguringPrintProduct(true)}
            className="purchasing-options-item-btn"
            btnStyle="link"
          >
            {accountCard ? `View & Edit` : `Edit configuration`}
          </Button>
        )}
      </div>
      {configuringPrintProduct && accountCard && (
        <PrintProductConfiguratorModal
          forceOpen
          controlsDisabled
          printProductId={photoPricingPrintProduct.print_product.id}
          printProductSizeId={photoPricingPrintProduct.print_product_size.id}
          photo={photo}
          onClose={() => {
            setConfiguringPrintProduct(false);
          }}
          onComplete={() => {
            setConfiguringPrintProduct(false);
          }}
          defaultAccountCard={accountCard}
          buttonText="Update"
          albumMembershipGroup={albumMembershipGroup}
          identity={identity}
          printBackgrounds={printBackgrounds}
        />
      )}
      {configuringPrintProduct && !accountCard && roesConfiguration && (
        <PrintProductSizeRoesConfiguratorModal
          isInCart
          forceOpen
          photo={photo}
          defaultConfiguration={roesConfiguration}
          printProduct={photoPricingPrintProduct.print_product}
          printProductSize={photoPricingPrintProduct.print_product_size}
          albumMembershipGroup={albumMembershipGroup}
          isAllPhotos={isAllPhotos}
          onComplete={({ roesConfiguration: newConfiguration }) => {
            onUpdate(item.id, {
              roesConfiguration: newConfiguration,
            });
            setConfiguringPrintProduct(false);
          }}
          onClose={() => {
            setConfiguringPrintProduct(false);
          }}
        />
      )}
    </Fragment>
  );
};

PhotoPricingPrintProductCartItem.propTypes = propTypes;

export default PhotoPricingPrintProductCartItem;
